// Task pane: sign in (with forced-rotation handling), configure the backend
// URL, browse the live function catalog, and search (the Search tab,
// searchPane.ts, mounted after sign-in). The JWT it stores is read by the
// =LSEG.* custom functions via OfficeRuntime.storage.

import {
  login,
  loginWithMicrosoft,
  isSsoSupported,
  me,
  logout,
  changePassword,
  listFunctions,
  ApiError,
  FunctionInfo,
} from "../lib/api";
import { getBackendUrl, setBackendUrl, getAccessToken } from "../lib/auth";
import { mountSearchPane, resetSearchPane, showTab } from "./searchPane";
import { mountRefreshPane } from "./refreshPane";
import { NoticeKind, onPaneSection, PaneSection } from "./paneSections";

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

function show(id: string, visible: boolean): void {
  const el = document.getElementById(id);
  if (el) el.hidden = !visible;
}

function setStatus(online: boolean, text: string): void {
  const chip = $("status-chip");
  chip.textContent = text;
  chip.className = "chip " + (online ? "chip-on" : "chip-off");
}

function msg(id: string, text: string, kind: "error" | "ok" | "" = ""): void {
  const el = $(id);
  el.textContent = text;
  el.className = "msg" + (kind ? ` msg-${kind}` : "");
  el.hidden = !text;
}

function setView(view: "login" | "rotate" | "main"): void {
  show("view-login", view === "login");
  show("view-rotate", view === "rotate");
  show("view-main", view === "main");
}

/**
 * A section the EAV ribbon tab asked for (commands.ts). Search needs a
 * session, so signed out it shows the sign-in instead; Account is the sign-in,
 * the password change or the signed-in bar, whichever is up; Settings is the
 * backend URL; a notice is a ribbon command's message, at the top (red for a failure).
 */
function showSection(section: PaneSection, text?: string, kind: NoticeKind = "error"): void {
  if (section === "notice") {
    msg("ribbon-notice", text ?? "", kind === "error" ? "error" : "");
    $("app").scrollIntoView({ block: "start" });
    return;
  }
  show("ribbon-notice", false);
  const signedIn = !$("view-main").hidden;
  if (section === "search" && signedIn) {
    showTab("search");
    return;
  }
  if (section === "settings") {
    const url = $<HTMLInputElement>("backend-url");
    url.scrollIntoView({ block: "center" });
    url.focus();
    return;
  }
  const target = signedIn ? "logout" : $("view-rotate").hidden ? "login-username" : "rotate-current";
  $("app").scrollIntoView({ block: "start" });
  $(target).focus();
}

async function showSignedIn(username: string): Promise<void> {
  $("who-name").textContent = username;
  setStatus(true, "signed in");
  setView("main");
  mountSearchPane({ onSignedOut: showSignedOut });
  await renderFunctions();
}

/**
 * The add-in's session ended under a search or a Find RICs run (api.ts has
 * cleared the tokens): the pane's signed-out state, never "Search failed".
 */
function showSignedOut(e: unknown): void {
  cancelResumeRetry();
  resetSearchPane();
  functionsRendered = false;
  setStatus(false, "offline");
  setView("login");
  const m = e instanceof ApiError && e.message ? e.message : "Session expired — sign in again.";
  msg("login-error", m, "error");
}

// --- Function catalog ------------------------------------------------------

let functionsRendered = false;

// Excel function namespace — must match manifest.xml Functions.Namespace.
const NS = "EAV";

// RtStream is an add-in-only streaming function — it doesn't go through
// /api/evaluate, so it isn't in the server's /api/functions catalog. We add it
// to the panel client-side, right after RtGet.
const RTSTREAM_INFO: FunctionInfo = {
  name: "RtStream",
  aliases: [],
  summary:
    "LIVE — streaming realtime prices. The spilled grid (one row per RIC) updates continuously in place as ticks arrive; no recalculation. Clear the cell to stop the stream.",
  signature: "RtStream(instruments, fields)",
  examples: [
    '=RtStream("VOD.L,AAPL.O", "BID;ASK;TRDPRC_1")',
    '=RtStream("EUR=,USD=,GBP=", "BID;ASK")',
  ],
};

async function renderFunctions(): Promise<void> {
  if (functionsRendered) return;
  const container = $("functions");
  container.textContent = "Loading…";
  let fns: FunctionInfo[];
  try {
    fns = await listFunctions();
  } catch (e) {
    container.textContent = `Could not load functions: ${(e as Error).message}`;
    return;
  }
  // Inject RtStream right after RtGet (or at the end if RtGet isn't present).
  const list = [...fns];
  const rtgetIdx = list.findIndex((f) => f.name.toUpperCase() === "RTGET");
  list.splice(rtgetIdx >= 0 ? rtgetIdx + 1 : list.length, 0, RTSTREAM_INFO);

  container.innerHTML = "";
  for (const f of list) {
    container.appendChild(functionCard(f));
  }
  functionsRendered = true;
}

function functionCard(f: FunctionInfo): HTMLElement {
  const card = document.createElement("div");
  card.className = "fn";

  const head = document.createElement("div");
  head.className = "fn-head";
  head.innerHTML = `<span class="fn-name">=${NS}.${f.name.toUpperCase()}</span><span class="fn-sig">${escapeHtml(
    f.signature
  )}</span>`;

  const body = document.createElement("div");
  body.className = "fn-body";
  body.hidden = true;

  const summary = document.createElement("div");
  summary.className = "fn-summary";
  summary.textContent = f.summary;
  body.appendChild(summary);

  if (f.aliases.length) {
    const aliases = document.createElement("div");
    aliases.className = "fn-aliases";
    aliases.textContent = "aliases: " + f.aliases.map((a) => `=${NS}.${a.toUpperCase()}`).join(", ");
    body.appendChild(aliases);
  }

  for (const ex of f.examples) {
    const exEl = document.createElement("div");
    exEl.className = "fn-example";
    // The interface examples use bare =NAME(...); show them under the add-in
    // namespace so they're paste-ready as Excel custom functions.
    const namespaced = ex.replace(/^=\s*/, `=${NS}.`);
    exEl.textContent = namespaced;
    exEl.title = "Click to copy";
    exEl.addEventListener("click", () => copyText(namespaced));
    body.appendChild(exEl);
  }

  head.addEventListener("click", () => {
    body.hidden = !body.hidden;
  });

  card.appendChild(head);
  card.appendChild(body);
  return card;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    /* clipboard may be blocked in some hosts; ignore */
  }
}

// --- Handlers --------------------------------------------------------------

function loginErr(e: unknown): string {
  const err = e as ApiError;
  if (err?.status === 401) return "Invalid credentials.";
  if (err?.status === 0) return err.message;
  return err?.message || "Sign-in failed.";
}

/** Stop the "reconnecting" retry of the boot-time session check (the user signed in or out meanwhile). */
function cancelResumeRetry(): void {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
}

async function onLogin(ev: Event): Promise<void> {
  ev.preventDefault();
  cancelResumeRetry();
  msg("login-error", "");
  const username = ($("login-username") as HTMLInputElement).value.trim();
  const password = ($("login-password") as HTMLInputElement).value;
  const btn = $("login-submit") as HTMLButtonElement;
  btn.disabled = true;
  try {
    const user = await login(username, password);
    ($("login-password") as HTMLInputElement).value = "";
    if (user.mustChangePassword) {
      setView("rotate");
      setStatus(false, "rotate pw");
    } else {
      await showSignedIn(user.username);
    }
  } catch (e) {
    msg("login-error", loginErr(e), "error");
  } finally {
    btn.disabled = false;
  }
}

async function onMicrosoftLogin(): Promise<void> {
  cancelResumeRetry();
  msg("login-error", "");
  const btn = $("login-ms") as HTMLButtonElement;
  btn.disabled = true;
  try {
    const user = await loginWithMicrosoft();
    if (user.mustChangePassword) {
      setView("rotate");
      setStatus(false, "rotate pw");
    } else {
      await showSignedIn(user.username);
    }
  } catch (e) {
    msg("login-error", msLoginErr(e), "error");
  } finally {
    btn.disabled = false;
  }
}

function msLoginErr(e: unknown): string {
  if (e instanceof ApiError) return e.message; // backend: rejected / pending approval
  const code = (e as { code?: number } | null)?.code;
  if (code === 13003) {
    return "Microsoft sign-in isn't available in this Office version — use your username and password.";
  }
  if (code === 13001 || code === 13002) return "Microsoft sign-in was cancelled.";
  const m = (e as { message?: string } | null)?.message;
  return m || "Microsoft sign-in failed. Try your username and password.";
}

async function onRotate(ev: Event): Promise<void> {
  ev.preventDefault();
  msg("rotate-error", "");
  const current = ($("rotate-current") as HTMLInputElement).value;
  const next = ($("rotate-new") as HTMLInputElement).value;
  const confirm = ($("rotate-confirm") as HTMLInputElement).value;
  if (next.length < 4) {
    msg("rotate-error", "New password must be at least 4 characters.", "error");
    return;
  }
  if (next !== confirm) {
    msg("rotate-error", "New password and confirmation do not match.", "error");
    return;
  }
  const btn = $("rotate-submit") as HTMLButtonElement;
  btn.disabled = true;
  try {
    await changePassword(current, next);
    ($("rotate-current") as HTMLInputElement).value = "";
    ($("rotate-new") as HTMLInputElement).value = "";
    ($("rotate-confirm") as HTMLInputElement).value = "";
    const user = await me();
    await showSignedIn(user.username);
  } catch (e) {
    msg("rotate-error", (e as ApiError).message || "Could not change password.", "error");
  } finally {
    btn.disabled = false;
  }
}

async function onLogout(): Promise<void> {
  cancelResumeRetry();
  await logout();
  resetSearchPane();
  functionsRendered = false;
  setStatus(false, "offline");
  setView("login");
}

async function onSaveBackend(ev: Event): Promise<void> {
  ev.preventDefault();
  const url = ($("backend-url") as HTMLInputElement).value.trim();
  if (!url) return;
  await setBackendUrl(url);
  msg("backend-msg", `Backend set to ${await getBackendUrl()}`, "ok");
}

// --- Boot ------------------------------------------------------------------

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.Excel) {
    document.body.innerHTML =
      '<p style="padding:16px">This add-in runs in Excel.</p>';
    return;
  }

  ($("backend-url") as HTMLInputElement).value = await getBackendUrl();

  // Add-in version (injected from manifest.xml at build time).
  const ver = (process.env.ADDIN_VERSION || "").split(".").slice(0, 3).join(".");
  $("version").textContent = ver ? `v${ver}` : "";

  $("login-form").addEventListener("submit", onLogin);
  // Microsoft 365 SSO — only offer it where the Office host supports it.
  show("login-ms", isSsoSupported());
  $("login-ms").addEventListener("click", onMicrosoftLogin);
  $("rotate-form").addEventListener("submit", onRotate);
  $("logout").addEventListener("click", onLogout);
  $("backend-form").addEventListener("submit", onSaveBackend);
  mountRefreshPane();
  onPaneSection(showSection);

  // Resume an existing session if a token is already stored.
  await resumeSession();
});

const RESUME_RETRY_MS = 15000;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;

/** The stored session is worth keeping through a server/network hiccup (api.ts keeps the tokens then). */
function isTransientFailure(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  return e.status === 0 || e.status === 429 || e.status >= 500;
}

async function resumeSession(): Promise<void> {
  resumeTimer = null;
  const token = await getAccessToken();
  if (token) {
    try {
      const user = await me();
      msg("login-error", "");
      if (user.mustChangePassword) {
        setView("rotate");
        setStatus(false, "rotate pw");
      } else {
        await showSignedIn(user.username);
      }
      return;
    } catch (e) {
      if (isTransientFailure(e) && (await getAccessToken())) {
        // Backend/database trouble, not a rejected session: the tokens are still
        // stored (and still serve =LSEG.* cells), so do not look signed out for
        // good — say so and try again.
        setView("login");
        setStatus(false, "reconnecting");
        msg("login-error", "LSEG backend temporarily unavailable — your session is kept, retrying…", "error");
        if (!resumeTimer) resumeTimer = setTimeout(() => void resumeSession(), RESUME_RETRY_MS);
        return;
      }
      // token rejected (api.ts cleared it) — fall through to login
    }
  }
  setView("login");
  setStatus(false, "offline");
}
